import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import React, { Component } from 'react';
import { Col, Row, Tabs, Tab } from "react-bootstrap";
import { Trash } from "react-feather";
import { apiFetch, API } from "../../services/api";
import { lotteryForm, lotteryBody, validateLottery } from "../../helpers/challengeShop";

// Barème par défaut (miroir de model.DefaultChallengeScoring côté back).
// Depuis le 23/09/2026, cliquer sur « partager » ne rapporte plus de points
// (share et firstShareBonus à 0) ; les partages restent comptés.
export const DEFAULT_SCORING = {
    registration: 10, share: 0, shareMaxPerDay: 0, visit: 1, visitMaxPerDay: 50,
    signup: 20, signupMaxTotal: 0, firstShareBonus: 0,
};

const SCORING_FIELDS = [
    ["registration", "Points à l'inscription"],
    ["share", "Points par partage"],
    ["shareMaxPerDay", "Partages comptés par jour (max)"],
    ["visit", "Points par visiteur"],
    ["visitMaxPerDay", "Plafond de points « visites » par jour"],
    ["signup", "Points par compte créé"],
    ["signupMaxTotal", "Plafond de comptes comptés (0 = illimité)"],
    ["firstShareBonus", "Bonus au premier partage"],
];

const LANGS = ["fr", "es"];

const emptyTexts = () => ({ title: "", intro: "", rules: "", shareText: "", regulation: "" });
const emptyTier = () => ({ points: "", rewardId: "", label: { fr: "", es: "" }, image: "", quantity: 0, grade: "", description: { fr: "", es: "" } });
const emptyDraw = () => ({ date: "", cutoffHour: 21, prizes: [emptyPrize()] });
const emptyPrize = () => ({ id: "", label: { fr: "", es: "" }, description: { fr: "", es: "" }, image: "", value: "", winners: 1, basket: false });
const emptyVisual = () => ({ date: "", imageURL: { fr: "", es: "" }, caption: { fr: "", es: "" } });

// « 2026-11-01T00:00:00Z » → « 2026-11-01 » (champ date).
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");

// Upload d'une image vers S3 via le back_api ; renvoie l'URL publique.
function uploadImage(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = reject;
        reader.onload = () => {
            apiFetch(API.back + '/upload-file', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ blob: reader.result, fileName: "challenges/" + file.name }),
            })
                .then(res => res.json())
                .then(result => resolve(result.message))
                .catch(reject);
        };
    });
}

// Modale de création / édition d'un challenge de partage. Le statut n'est
// pas éditable ici (actions Ouvrir / Démarrer / Clôturer dans la liste).
export default class AddChallengeModal extends Component {
    constructor(props) {
        super(props);
        const c = props.selectedChallenge || {};
        const texts = {};
        LANGS.forEach(l => { texts[l] = { ...emptyTexts(), ...((c.texts || {})[l] || {}) }; });
        this.state = {
            id: c.id || "",
            name: c.name || "",
            slug: c.slug || "",
            language: c.language || "all",
            beginDate: toDateInput(c.beginDate),
            endDate: toDateInput(c.endDate),
            leaderboardSize: c.leaderboardSize || 10,
            dailyReminder: !!c.dailyReminder,
            // Mode boutique (spec 2026-10-16) : "" = paliers, "shop" = lots achetables en miles + loterie.
            mechanics: c.mechanics || "",
            lottery: lotteryForm(c.lottery),
            coverImage: { fr: (c.coverImage || {}).fr || "", es: (c.coverImage || {}).es || "" },
            texts,
            scoring: { ...DEFAULT_SCORING, ...(c.scoring || {}) },
            tiers: (c.tiers && c.tiers.length ? c.tiers : []).map(t => ({
                points: t.points, rewardId: t.rewardId || "", image: t.image || "", quantity: t.quantity || 0, grade: t.grade || "",
                label: { fr: (t.label || {}).fr || "", es: (t.label || {}).es || "" },
                description: { fr: (t.description || {}).fr || "", es: (t.description || {}).es || "" },
            })),
            visuals: (c.dailyVisuals || []).map(v => ({
                date: v.date || "",
                imageURL: { fr: (v.imageURL || {}).fr || "", es: (v.imageURL || {}).es || "" },
                caption: { fr: (v.caption || {}).fr || "", es: (v.caption || {}).es || "" },
            })),
            uploading: null,
            loading: false,
            error: null,
            saved: false,
        };
    }

    setText = (lang, field, value) => this.setState(prev => ({
        texts: { ...prev.texts, [lang]: { ...prev.texts[lang], [field]: value } },
    }));

    setScoring = (field, value) => this.setState(prev => ({
        scoring: { ...prev.scoring, [field]: value === "" ? "" : parseInt(value, 10) },
    }));

    setRow = (key, index, updater) => this.setState(prev => ({
        [key]: prev[key].map((row, i) => (i === index ? updater(row) : row)),
    }));

    removeRow = (key, index) => this.setState(prev => ({ [key]: prev[key].filter((_, i) => i !== index) }));

    handleUpload = (key, index, lang, file) => {
        if (!file) return;
        const tag = `${key}-${index}-${lang}`;
        this.setState({ uploading: tag });
        uploadImage(file)
            .then(url => {
                if (key === "tiers") {
                    this.setRow("tiers", index, row => ({ ...row, image: url }));
                } else if (key === "cover") {
                    this.setState(prev => ({ coverImage: { ...prev.coverImage, [lang]: url } }));
                } else {
                    this.setRow("visuals", index, row => ({ ...row, imageURL: { ...row.imageURL, [lang]: url } }));
                }
                this.setState({ uploading: null });
            })
            .catch(err => {
                console.log(err);
                this.setState({ uploading: null, error: "Échec de l'envoi de l'image" });
            });
    };

    validate = () => {
        const s = this.state;
        if (!s.name.trim()) return "Le nom est obligatoire";
        if (!/^[a-z0-9-]+$/.test(s.slug.trim().toLowerCase())) return "Le slug ne doit contenir que des lettres minuscules, chiffres et tirets";
        if (!s.beginDate || !s.endDate) return "Les dates de début et de fin sont obligatoires";
        if (s.endDate <= s.beginDate) return "La date de fin doit être après la date de début";
        if (!s.texts.fr.title.trim()) return "Le titre FR est obligatoire";
        for (const [field, label] of SCORING_FIELDS) {
            if (s.scoring[field] === "" || Number.isNaN(s.scoring[field]) || s.scoring[field] < 0) return `Barème : « ${label} » doit être un entier ≥ 0`;
        }
        const seen = {};
        for (let i = 0; i < s.tiers.length; i++) {
            const t = s.tiers[i];
            const pts = parseInt(t.points, 10);
            if (Number.isNaN(pts) || pts <= 0) return `Palier ${i + 1} : points > 0 requis`;
            if (!t.rewardId) return `Palier ${i + 1} : choisissez une récompense`;
            if (!t.label.fr.trim()) return `Palier ${i + 1} : libellé FR obligatoire`;
            const qty = parseInt(t.quantity, 10);
            if (t.quantity !== "" && (Number.isNaN(qty) || qty < 0)) return `Palier ${i + 1} : stock = entier ≥ 0 (0 = illimité)`;
            if (seen[pts]) return `Palier ${i + 1} : deux paliers ont le même nombre de points`;
            seen[pts] = true;
        }
        if (s.mechanics === "shop") {
            const lotteryError = validateLottery(s.lottery);
            if (lotteryError) return lotteryError;
        }
        for (let i = 0; i < s.visuals.length; i++) {
            const v = s.visuals[i];
            if (!/^\d{4}-\d{2}-\d{2}$/.test(v.date)) return `Visuel ${i + 1} : date invalide`;
            if (!v.imageURL.fr) return `Visuel ${i + 1} : image FR obligatoire`;
        }
        return null;
    };

    handleSubmit = () => {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }
        const s = this.state;
        const texts = {};
        LANGS.forEach(l => {
            const t = s.texts[l];
            if (l === "fr" || t.title.trim() || t.intro.trim() || t.rules.trim() || t.shareText.trim() || t.regulation.trim()) texts[l] = t;
        });
        const body = {
            id: s.id,
            name: s.name.trim(),
            slug: s.slug.trim().toLowerCase(),
            language: s.language,
            beginDate: s.beginDate + "T00:00:00Z",
            endDate: s.endDate + "T23:59:59Z",
            leaderboardSize: parseInt(s.leaderboardSize, 10) || 10,
            dailyReminder: s.dailyReminder,
            mechanics: s.mechanics,
            lottery: lotteryBody(s.lottery),
            coverImage: { ...(s.coverImage.fr ? { fr: s.coverImage.fr } : {}), ...(s.coverImage.es ? { es: s.coverImage.es } : {}) },
            texts,
            scoring: s.scoring,
            tiers: s.tiers.map(t => ({
                points: parseInt(t.points, 10), rewardId: t.rewardId, image: t.image,
                quantity: parseInt(t.quantity, 10) || 0, grade: (t.grade || "").trim(),
                description: { ...(t.description.fr.trim() ? { fr: t.description.fr.trim() } : {}), ...(t.description.es.trim() ? { es: t.description.es.trim() } : {}) },
                label: { fr: t.label.fr.trim(), ...(t.label.es.trim() ? { es: t.label.es.trim() } : {}) },
            })),
            dailyVisuals: s.visuals.map(v => ({
                date: v.date,
                imageURL: { fr: v.imageURL.fr, ...(v.imageURL.es ? { es: v.imageURL.es } : {}) },
                caption: { fr: v.caption.fr, ...(v.caption.es ? { es: v.caption.es } : {}) },
            })),
        };
        this.setState({ loading: true, error: null });
        apiFetch(API.back + '/challenge', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        })
            .then(async response => {
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || "Erreur");
                return result;
            })
            .then(async result => {
                if (this.props.refresh) await this.props.refresh();
                this.setState({ id: result.id || s.id, loading: false, saved: true });
            })
            .catch(err => {
                console.log(err);
                this.setState({ loading: false, error: err.message || "Erreur lors de l'enregistrement" });
            });
    };

    renderTexts = (lang) => (
        <>
            {[["title", "Titre", "input"], ["intro", "Introduction", "textarea"], ["rules", "Règles", "textarea"], ["shareText", "Texte de partage (accompagne le lien)", "input"],
              ["regulation", "Règlement (à faire défiler et accepter avant l'inscription)", "textarea"]].map(([field, label, kind]) => (
                <Form.Group className="mb-3" key={field}>
                    <Form.Label>{label} ({lang.toUpperCase()}) :</Form.Label>
                    <Form.Control as={kind === "textarea" ? "textarea" : undefined} rows={kind === "textarea" ? (field === "regulation" ? 12 : 4) : undefined}
                                  type="text" value={this.state.texts[lang][field]}
                                  onChange={(e) => this.setText(lang, field, e.target.value)} />
                    {field === "regulation" ? (
                        <Form.Text className="text-muted">
                            Vide dans toutes les langues = pas de règlement demandé. Sinon, le participant doit dérouler le texte en entier et cocher
                            la case avant de s'inscrire. Toute modification du texte (même une virgule) redemande l'acceptation aux inscrits, qui ne
                            peuvent plus partager tant qu'ils n'ont pas ré-accepté.
                        </Form.Text>
                    ) : null}
                </Form.Group>
            ))}
        </>
    );

    setLottery = (patch) => this.setState(prev => ({ lottery: { ...prev.lottery, ...patch } }));

    setDraw = (index, updater) => this.setState(prev => ({
        lottery: { ...prev.lottery, draws: prev.lottery.draws.map((d, i) => (i === index ? updater(d) : d)) },
    }));

    setPrize = (di, pi, updater) => this.setDraw(di, d => ({ ...d, prizes: d.prizes.map((p, j) => (j === pi ? updater(p) : p)) }));

    uploadPrizeImage = (di, pi, file) => {
        if (!file) return;
        this.setState({ uploading: `prize-${di}-${pi}` });
        uploadImage(file)
            .then(url => { this.setPrize(di, pi, p => ({ ...p, image: url })); this.setState({ uploading: null }); })
            .catch(() => this.setState({ uploading: null, error: "Échec de l'envoi de l'image" }));
    };

    renderLottery() {
        const l = this.state.lottery;
        const num = (field, label, help) => (
            <Col sm={3}>
                <Form.Group className="mb-3">
                    <Form.Label>{label}</Form.Label>
                    <Form.Control type="number" min={0} value={l[field]} onChange={(e) => this.setLottery({ [field]: e.target.value })} />
                    {help ? <Form.Text className="text-muted">{help}</Form.Text> : null}
                </Form.Group>
            </Col>
        );
        return (
            <>
                <Row>
                    <Col sm={3}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ouverture des tickets :</Form.Label>
                            <Form.Control type="date" value={l.opensAt} onChange={(e) => this.setLottery({ opensAt: e.target.value })} />
                            <Form.Text className="text-muted">à minuit (Paris)</Form.Text>
                        </Form.Group>
                    </Col>
                    {num("ticketPriceMiles", "Ticket en miles :")}
                    {num("ticketPriceCoins", "Ticket en TRCoins :", "0 = pas de paiement en TRCoins")}
                    {num("maxTicketsPerDraw", "Tickets max / personne / tirage :", "0 = illimité")}
                </Row>
                {l.draws.map((d, di) => (
                    <div key={di} style={{ border: "1px solid #e0e0e0", borderRadius: 4, padding: 12, marginBottom: 12 }}>
                        <Row>
                            <Col sm={3}>
                                <Form.Group className="mb-2">
                                    <Form.Label>Jour du tirage :</Form.Label>
                                    <Form.Control type="date" value={d.date} onChange={(e) => this.setDraw(di, x => ({ ...x, date: e.target.value }))} />
                                </Form.Group>
                            </Col>
                            <Col sm={3}>
                                <Form.Group className="mb-2">
                                    <Form.Label>Clôture des tickets (h, Paris) :</Form.Label>
                                    <Form.Control type="number" min={0} max={23} value={d.cutoffHour} onChange={(e) => this.setDraw(di, x => ({ ...x, cutoffHour: e.target.value }))} />
                                </Form.Group>
                            </Col>
                            <Col sm={6} style={{ textAlign: "right" }}>
                                <Button variant="outline-danger" size="sm" onClick={() => this.setLottery({ draws: l.draws.filter((_, i) => i !== di) })}><Trash size={14} /> Supprimer le tirage</Button>
                            </Col>
                        </Row>
                        {d.prizes.map((p, pi) => (
                            <Row key={pi} style={{ background: "#fafafa", padding: "8px 0", marginBottom: 6 }}>
                                <Col sm={2}>
                                    <Form.Label>Identifiant :</Form.Label>
                                    <Form.Control size="sm" value={p.id} placeholder="evo-sl" onChange={(e) => this.setPrize(di, pi, x => ({ ...x, id: e.target.value }))} />
                                </Col>
                                <Col sm={3}>
                                    <Form.Label>Lot (FR / ES) :</Form.Label>
                                    <Form.Control size="sm" value={p.label.fr} placeholder="FR" onChange={(e) => this.setPrize(di, pi, x => ({ ...x, label: { ...x.label, fr: e.target.value } }))} />
                                    <Form.Control size="sm" value={p.label.es} placeholder="ES" onChange={(e) => this.setPrize(di, pi, x => ({ ...x, label: { ...x.label, es: e.target.value } }))} />
                                </Col>
                                <Col sm={1}>
                                    <Form.Label>Gagnants :</Form.Label>
                                    <Form.Control size="sm" type="number" min={1} value={p.winners} onChange={(e) => this.setPrize(di, pi, x => ({ ...x, winners: e.target.value }))} />
                                </Col>
                                <Col sm={2}>
                                    <Form.Label>Valeur affichée :</Form.Label>
                                    <Form.Control size="sm" value={p.value} placeholder="150 €" onChange={(e) => this.setPrize(di, pi, x => ({ ...x, value: e.target.value }))} />
                                    <Form.Check type="checkbox" id={`basket-${di}-${pi}`} label="Panier 1 minute" checked={p.basket}
                                                onChange={(e) => this.setPrize(di, pi, x => ({ ...x, basket: e.target.checked }))} />
                                </Col>
                                <Col sm={3}>
                                    <Form.Label>Image :</Form.Label>
                                    {p.image ? <img src={p.image} alt="" style={{ height: 40, display: "block" }} /> : null}
                                    <Form.Control size="sm" type="file" accept="image/*" disabled={this.state.uploading === `prize-${di}-${pi}`}
                                                  onChange={(e) => this.uploadPrizeImage(di, pi, e.target.files[0])} />
                                </Col>
                                <Col sm={1} style={{ display: "flex", alignItems: "flex-end" }}>
                                    <Button variant="link" size="sm" onClick={() => this.setDraw(di, x => ({ ...x, prizes: x.prizes.filter((_, j) => j !== pi) }))}><Trash size={14} /></Button>
                                </Col>
                            </Row>
                        ))}
                        <Button variant="outline-secondary" size="sm" onClick={() => this.setDraw(di, x => ({ ...x, prizes: [...x.prizes, emptyPrize()] }))}>+ Lot</Button>
                    </div>
                ))}
                <Button variant="outline-secondary" size="sm" onClick={() => this.setLottery({ draws: [...l.draws, emptyDraw()] })}>+ Tirage</Button>
                <Form.Text className="text-muted">
                    Un ticket vaut pour UN tirage, payé en miles ou en TRCoins, jusqu'à l'heure de clôture du jour. Le tirage se lance dans
                    « Boutique &amp; loterie » (définitif) ; un lot « Panier 1 minute » envoie au gagnant un lien personnel de 60 s.
                </Form.Text>
            </>
        );
    }

    render() {
        const rewards = this.props.rewards || [];
        const { uploading } = this.state;
        return (
            <Modal show={this.props.isOpen} onHide={this.props.closeModal} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{this.state.id ? "Modifier" : "Créer"} un challenge</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Tabs defaultActiveKey="general" id="challenge-tabs" className="mb-3">
                            <Tab eventKey="general" title="Général">
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom (interne, tag Mailchimp « Challenge &lt;nom&gt; ») :</Form.Label>
                                            <Form.Control type="text" value={this.state.name} onChange={(e) => this.setState({ name: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Slug (URL /challenge/&lt;slug&gt;) :</Form.Label>
                                            <Form.Control type="text" value={this.state.slug} disabled={!!this.state.id}
                                                          onChange={(e) => this.setState({ slug: e.target.value })} />
                                            {this.state.id ? <Form.Text className="text-muted">Identifiant : {this.state.id}</Form.Text> : null}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Début :</Form.Label>
                                            <Form.Control type="date" value={this.state.beginDate} onChange={(e) => this.setState({ beginDate: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Fin (inclus) :</Form.Label>
                                            <Form.Control type="date" value={this.state.endDate} onChange={(e) => this.setState({ endDate: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Marché :</Form.Label>
                                            <Form.Control as="select" custom value={this.state.language} onChange={(e) => this.setState({ language: e.target.value })}>
                                                <option value="all">FR + ES</option>
                                                <option value="fr">FR seulement</option>
                                                <option value="es">ES seulement</option>
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Taille du classement public :</Form.Label>
                                            <Form.Control type="number" min={1} max={100} value={this.state.leaderboardSize}
                                                          onChange={(e) => this.setState({ leaderboardSize: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Mécanique :</Form.Label>
                                    <Form.Control as="select" custom value={this.state.mechanics} onChange={(e) => this.setState({ mechanics: e.target.value })}>
                                        <option value="">Paliers (atteindre X miles débloque le lot)</option>
                                        <option value="shop">Boutique + loterie (les miles se dépensent : lots en stock, tickets de tirage)</option>
                                    </Form.Control>
                                    {this.state.mechanics === "shop" ? <Form.Text className="text-muted">Les « paliers » deviennent les lots de la boutique : points = prix en miles, stock = quantité. Le classement reste sur les miles gagnés.</Form.Text> : null}
                                </Form.Group>
                                <Form.Check type="checkbox" id="challenge-daily-reminder" label="Rappel quotidien par push à 18 h (Paris) aux participants"
                                            checked={this.state.dailyReminder} onChange={(e) => this.setState({ dailyReminder: e.target.checked })} />
                                <Row style={{ marginTop: 16 }}>
                                    {LANGS.map(l => (
                                        <Col sm={6} key={l}>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Visuel de couverture {l.toUpperCase()} (bandeau de la page, visible de tous) :</Form.Label>
                                                <Form.Control type="file" accept="image/*" disabled={uploading === `cover-0-${l}`}
                                                              onChange={(e) => this.handleUpload("cover", 0, l, e.target.files[0])} />
                                                {this.state.coverImage[l] ? (
                                                    <Form.Text className="text-muted">
                                                        <a href={this.state.coverImage[l]} target="_blank" rel="noreferrer">voir l'image</a>
                                                        {" · "}<a href="#retirer" onClick={(e) => { e.preventDefault(); this.setState(prev => ({ coverImage: { ...prev.coverImage, [l]: "" } })); }}>retirer</a>
                                                    </Form.Text>
                                                ) : <Form.Text className="text-muted">Format conseillé : paysage (ex. 1200×800). ES vide = repli sur FR.</Form.Text>}
                                            </Form.Group>
                                        </Col>
                                    ))}
                                </Row>
                                <Form.Text className="text-muted">Les visuels du jour (onglet « Visuels ») ne sont montrés qu'aux inscrits, dans leur espace de partage.</Form.Text>
                            </Tab>
                            <Tab eventKey="fr" title="Textes FR">{this.renderTexts("fr")}</Tab>
                            <Tab eventKey="es" title="Textes ES">
                                <Form.Text className="text-muted">Laisser vide pour retomber sur les textes FR.</Form.Text>
                                {this.renderTexts("es")}
                            </Tab>
                            <Tab eventKey="scoring" title="Barème">
                                <Row>
                                    {SCORING_FIELDS.map(([field, label]) => (
                                        <Col sm={6} key={field}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{label} :</Form.Label>
                                                <Form.Control type="number" min={0} step={1} value={this.state.scoring[field]}
                                                              onChange={(e) => this.setScoring(field, e.target.value)} />
                                            </Form.Group>
                                        </Col>
                                    ))}
                                </Row>
                                <Form.Text className="text-muted">
                                    Les visites sont des visiteurs distincts venus par le lien (auto-visites exclues), les comptes créés
                                    doivent être activés. Les points sont recalculés toutes les 15 min par le robot challenge-scorer.
                                </Form.Text>
                            </Tab>
                            <Tab eventKey="tiers" title={`${this.state.mechanics === "shop" ? "Boutique" : "Paliers"} (${this.state.tiers.length})`}>
                                {this.state.tiers.map((tier, index) => (
                                    <div key={index} style={{ border: "1px solid #e0e0e0", borderRadius: 4, padding: 12, marginBottom: 12 }}>
                                        <Row>
                                            <Col sm={2}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Points :</Form.Label>
                                                    <Form.Control type="number" min={1} value={tier.points}
                                                                  onChange={(e) => this.setRow("tiers", index, r => ({ ...r, points: e.target.value }))} />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={2}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Stock (0 = illimité) :</Form.Label>
                                                    <Form.Control type="number" min={0} value={tier.quantity}
                                                                  onChange={(e) => this.setRow("tiers", index, r => ({ ...r, quantity: e.target.value }))} />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={3}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Récompense :</Form.Label>
                                                    <Form.Control as="select" custom value={tier.rewardId}
                                                                  onChange={(e) => this.setRow("tiers", index, r => ({ ...r, rewardId: e.target.value }))}>
                                                        <option value="">Sélectionner...</option>
                                                        {tier.rewardId && !rewards.some(r => r.id === tier.rewardId)
                                                            ? <option value={tier.rewardId}>{tier.rewardId} (identifiant libre, hors liste des récompenses)</option> : null}
                                                        {rewards.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                                    </Form.Control>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Image (optionnelle) :</Form.Label>
                                                    <Form.Control type="file" accept="image/*" disabled={uploading === `tiers-${index}-fr`}
                                                                  onChange={(e) => this.handleUpload("tiers", index, "fr", e.target.files[0])} />
                                                    {tier.image ? <Form.Text className="text-muted"><a href={tier.image} target="_blank" rel="noreferrer">voir l'image</a></Form.Text> : null}
                                                </Form.Group>
                                            </Col>
                                            <Col sm={1}>
                                                <Button variant="secondary" style={{ marginTop: 30 }} onClick={() => this.removeRow("tiers", index)}><Trash size={10} /></Button>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={12}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>Grade du niveau (optionnel, ex. Jogger, Trailer, Finisher — affiché au-dessus du lot) :</Form.Label>
                                                    <Form.Control type="text" value={tier.grade}
                                                                  onChange={(e) => this.setRow("tiers", index, r => ({ ...r, grade: e.target.value }))} />
                                                </Form.Group>
                                            </Col>
                                            {LANGS.map(l => (
                                                <Col sm={6} key={l}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Lot {l.toUpperCase()} :</Form.Label>
                                                        <Form.Control type="text" value={tier.label[l]}
                                                                      onChange={(e) => this.setRow("tiers", index, r => ({ ...r, label: { ...r.label, [l]: e.target.value } }))} />
                                                    </Form.Group>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Description du lot {l.toUpperCase()} (popup sur le site) :</Form.Label>
                                                        <Form.Control as="textarea" rows={3} value={tier.description[l]}
                                                                      onChange={(e) => this.setRow("tiers", index, r => ({ ...r, description: { ...r.description, [l]: e.target.value } }))} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                ))}
                                <Button variant="secondary" onClick={() => this.setState(prev => ({ tiers: [...prev.tiers, emptyTier()] }))}>+ Ajouter un palier</Button>
                                <Form.Text className="text-muted">
                                    Stock : les lots vont aux premiers participants à atteindre le palier (ordre d'arrivée, exclus non servis) ; une fois le
                                    stock écoulé, le palier reste affiché « épuisé » sur le site et les suivants ne sont pas notifiés. Attribution posée par
                                    le robot challenge-scorer (15 min), visible dans l'export « lots » (colonne « lot attribué »).
                                </Form.Text>
                            </Tab>
                            {this.state.mechanics === "shop" ? (
                                <Tab eventKey="lottery" title={`Loterie (${this.state.lottery.draws.length})`}>{this.renderLottery()}</Tab>
                            ) : null}
                            <Tab eventKey="visuals" title={`Visuels (${this.state.visuals.length})`}>
                                <Form.Text className="text-muted">
                                    Un visuel par date : il est affiché du jour indiqué jusqu'au visuel suivant. L'image FR sert de repli à l'ES.
                                </Form.Text>
                                {this.state.visuals.map((visual, index) => (
                                    <div key={index} style={{ border: "1px solid #e0e0e0", borderRadius: 4, padding: 12, marginBottom: 12, marginTop: 8 }}>
                                        <Row>
                                            <Col sm={3}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>À partir du :</Form.Label>
                                                    <Form.Control type="date" value={visual.date}
                                                                  onChange={(e) => this.setRow("visuals", index, r => ({ ...r, date: e.target.value }))} />
                                                </Form.Group>
                                            </Col>
                                            {LANGS.map(l => (
                                                <Col sm={4} key={l}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Image {l.toUpperCase()} :</Form.Label>
                                                        <Form.Control type="file" accept="image/*" disabled={uploading === `visuals-${index}-${l}`}
                                                                      onChange={(e) => this.handleUpload("visuals", index, l, e.target.files[0])} />
                                                        {visual.imageURL[l] ? <Form.Text className="text-muted"><a href={visual.imageURL[l]} target="_blank" rel="noreferrer">voir l'image</a></Form.Text> : null}
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                            <Col sm={1}>
                                                <Button variant="secondary" style={{ marginTop: 30 }} onClick={() => this.removeRow("visuals", index)}><Trash size={10} /></Button>
                                            </Col>
                                        </Row>
                                        <Row>
                                            {LANGS.map(l => (
                                                <Col sm={6} key={l}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Légende {l.toUpperCase()} :</Form.Label>
                                                        <Form.Control type="text" value={visual.caption[l]}
                                                                      onChange={(e) => this.setRow("visuals", index, r => ({ ...r, caption: { ...r.caption, [l]: e.target.value } }))} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                ))}
                                <Button variant="secondary" onClick={() => this.setState(prev => ({ visuals: [...prev.visuals, emptyVisual()] }))}>+ Ajouter un visuel</Button>
                            </Tab>
                        </Tabs>
                        {uploading ? <p className="text-muted">Envoi de l'image…</p> : null}
                        {this.state.saved ? <p style={{ color: "#27ae60", marginTop: 12 }}>Challenge enregistré.</p> : null}
                        {this.state.error ? <p style={{ color: "#c0392b" }}>{this.state.error}</p> : null}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.props.closeModal}>{this.state.saved ? "Fermer" : "Annuler"}</Button>
                    <Button variant="primary" onClick={this.handleSubmit} disabled={this.state.loading || !!uploading}>
                        {this.state.loading ? "Enregistrement…" : "Valider"}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
